import { isMasked, pageVia, respondMs, scrollDepth, testHook, trailArea, trailKey, trailLinkShape, trailState, visibleLength, type TestHook, type SessionAction, type SessionTrail, type TrailArea } from './sessionTrail';
import { trailRoleWords } from './trailRoleWords';

/**
 * A ticket's timeline (TOADO-911): what the person saw and did, and what the
 * page logged, requested and threw, on one line in time order, ending at the
 * report. Shared by the web's Session window and the API's timeline route, which
 * MCP's `get_timeline` reads.
 *
 * Console lines and errors carry the wall-clock time they happened, and so do
 * a trail's pages and actions. A request carries `startedAt`, counted from the
 * capture's `browser.timeOrigin`: a widget report's page time origin, or an
 * extension capture's earliest request (TOADO-918). A request that cannot be
 * placed (an extension capture from before 0.2.0, say) is listed apart, in the
 * order it was captured, rather than guessed into the line.
 *
 * Everything here was written by the page or the visitor's browser, so every
 * time and every string is checked before it is used.
 */

export type TimelineKind = 'page' | 'action' | 'console' | 'error' | 'request' | 'report';

export interface TimelineEntry {
  /** Epoch ms; for a request that cannot be placed, null. */
  at: number | null;
  kind: TimelineKind;
  /** What happened, one line. */
  label: string;
  /** A request's status, an error's repeat count, a console line's level. */
  detail?: string;
  /** A rage click, or a click that may have done nothing. */
  flag?: 'rage click' | 'possibly dead click';
  /**
   * A Playwright locator for a clicked element, from its role and name alone:
   * `getByRole('button', { name: "Pay now" })` (TOADO-917). None where it has
   * no name, a name the redactors masked, or a role a locator cannot be sure
   * of. Built from what the trail already keeps; no path to the element is
   * recorded (TOADO-929). A field's change has none: its name may be a
   * placeholder or a name attribute rather than its label. A click with the
   * developer's test hook is found by it instead (TOADO-927): `getByTestId("pay")`
   * for `data-testid`, `locator('[data-cy="pay"]')` for the others.
   */
  locator?: string;
  /** Where a clicked link goes, as a shape: `/users/:id/edit`, or another site's host (TOADO-928). */
  goes?: string;
  /** A failed request (400 and up, or no answer), an error, or a console error. */
  failed: boolean;
  /**
   * On a request, console line or error: the action it followed, as the
   * action's own label, and how many milliseconds after it (TOADO-918).
   */
  after?: { label: string; ms: number };
  /** On a page: the furthest down it the person scrolled, in tenths (TOADO-923). */
  depth?: number;
  /** On a click or a key: how long the page took to answer it, when slow (TOADO-924). */
  respondMs?: number;
  /** On an action: what followed it (TOADO-918), counted, with the failures named. */
  followed?: Followed;
  /** Where the row is in its own list. */
  index: number;
}

/** What followed an action, until the next action or FOLLOW_WINDOW_MS (TOADO-918). */
export interface Followed {
  requests: number;
  console: number;
  errors: number;
  /** How many of them failed: requests of 400 and up or with no answer, errors, console errors. */
  failed: number;
  /** The failures by their labels, the first few, for get_timeline; the Session window counts them only. */
  failures: string[];
}

/** How long after an action what happened is counted as following it, unless another action came first. */
export const FOLLOW_WINDOW_MS = 3000;
/** The failures an action names; the rest are counted. */
const MAX_FOLLOWED_FAILURES = 3;

export interface Timeline {
  /** In time order, oldest first, with the report among them. */
  entries: TimelineEntry[];
  /** Requests with no time to place them by, in the order captured. */
  untimed: TimelineEntry[];
}

export interface TimelineInput {
  console?: unknown;
  errors?: unknown;
  network?: unknown;
  /** What a request's `startedAt` counts from (`browser.timeOrigin`). */
  timeOrigin?: unknown;
  /** A widget report's trail (`capture.session`). */
  session?: unknown;
  /** When it was reported (ISO). */
  reportedAt?: string;
}

/** The most rows the line holds: the newest, as the tabs keep the newest. */
export const MAX_TIMELINE_ENTRIES = 2000;
const MAX_LABEL = 140;
/** Epoch milliseconds from 2001 to 2286: anything else is not a time a page wrote. */
const isEpochMs = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 1e12 && value < 1e13;

export function buildTimeline(input: TimelineInput): Timeline {
  const timed: TimelineEntry[] = [];
  const untimed: TimelineEntry[] = [];
  const trail = input.session && typeof input.session === 'object' ? input.session as Partial<SessionTrail> : {};

  listOf<{ at?: unknown; url?: unknown; title?: unknown; via?: unknown; depth?: unknown }>(trail.pages).forEach((page, index) => {
    if (!isEpochMs(page.at)) return;
    const where = requestPath(page.url);
    const title = typeof page.title === 'string' && page.title.trim() ? ` (${page.title.trim()})` : '';
    const depth = scrollDepth(page.depth);
    timed.push({ at: page.at, kind: 'page', index, label: line(`${PAGE_VERBS[pageVia(page.via) ?? 'load']} ${where}${title}`), failed: false, ...(depth !== undefined ? { depth } : {}) });
  });

  const actions = listOf<SessionAction>(trail.actions);
  const ledTo = whereLinksLed(actions, listOf<{ at?: unknown; url?: unknown; via?: unknown }>(trail.pages));
  actions.forEach((action, index) => {
    if (!isEpochMs(action.at)) return;
    const entry = actionEntry(action, index, ledTo.get(index));
    if (entry) timed.push(entry);
  });

  listOf<{ timestamp?: unknown; level?: unknown; message?: unknown }>(input.console).forEach((row, index) => {
    if (!isEpochMs(row.timestamp)) return;
    timed.push({
      at: row.timestamp, kind: 'console', index,
      label: line(row.message), detail: typeof row.level === 'string' ? row.level : undefined,
      failed: row.level === 'error',
    });
  });

  listOf<{ timestamp?: unknown; message?: unknown; count?: unknown }>(input.errors).forEach((row, index) => {
    if (!isEpochMs(row.timestamp)) return;
    const count = typeof row.count === 'number' && row.count > 1 ? `×${Math.round(row.count)}` : undefined;
    timed.push({ at: row.timestamp, kind: 'error', index, label: line(row.message), detail: count, failed: true });
  });

  const origin = input.timeOrigin;
  listOf<{ method?: unknown; url?: unknown; status?: unknown; startedAt?: unknown }>(input.network).forEach((row, index) => {
    const status = typeof row.status === 'number' && Number.isFinite(row.status) ? row.status : undefined;
    const entry: TimelineEntry = {
      at: null, kind: 'request', index,
      label: line(`${typeof row.method === 'string' && row.method ? `${row.method} ` : ''}${requestPath(row.url)}`),
      detail: status === undefined ? undefined : status === 0 ? 'failed' : String(status),
      failed: status !== undefined && (status === 0 || status >= 400),
    };
    const started = row.startedAt;
    if (isEpochMs(origin) && typeof started === 'number' && Number.isFinite(started) && started >= 0 && started < 30 * 86_400_000) {
      timed.push({ ...entry, at: Math.round(origin + started) });
    } else {
      untimed.push(entry);
    }
  });

  const reported = reportMoment(trail, input.reportedAt);
  if (reported !== null) timed.push({ at: reported, kind: 'report', index: 0, label: 'Reported', failed: false });

  // Oldest first; on the same millisecond, in the order a page does them (it
  // opens, the person acts, a request goes, the console and errors follow),
  // and the report last.
  const order: Record<TimelineKind, number> = { page: 0, action: 1, request: 2, console: 3, error: 4, report: 5 };
  timed.sort((a, b) => (a.at! - b.at!) || (order[a.kind] - order[b.kind]) || (a.index - b.index));
  linkFollowers(timed);
  return { entries: timed.slice(-MAX_TIMELINE_ENTRIES), untimed: untimed.slice(-MAX_TIMELINE_ENTRIES) };
}

/**
 * Ties what the page did to the action it followed (TOADO-918): each request,
 * console line and error within FOLLOW_WINDOW_MS of an action, and before the
 * next action, page or report, names that action and how long after it came;
 * the action counts them and names its failures. Only by time: a request
 * started by a timer a second after a click is counted as the click's, which
 * is what a person reading the line would guess too.
 */
function linkFollowers(timed: TimelineEntry[]): void {
  let last: TimelineEntry | null = null;
  for (const entry of timed) {
    if (entry.kind === 'action' || entry.kind === 'page' || entry.kind === 'report') {
      last = entry.kind === 'action' ? entry : null;
      continue;
    }
    if (!last || entry.at! - last.at! > FOLLOW_WINDOW_MS) continue;
    entry.after = { label: last.label, ms: entry.at! - last.at! };
    const followed = last.followed ??= { requests: 0, console: 0, errors: 0, failed: 0, failures: [] };
    if (entry.kind === 'request') followed.requests += 1;
    else if (entry.kind === 'console') followed.console += 1;
    else followed.errors += 1;
    if (entry.failed) followed.failed += 1;
    if (entry.failed && followed.failures.length < MAX_FOLLOWED_FAILURES) {
      followed.failures.push(entry.detail && entry.kind === 'request' ? `${entry.label} ${entry.detail}` : entry.label);
    }
  }
}

/**
 * When it was reported, on the clock the rest of the line is on: a widget
 * report's own setup says it with its offset, the visitor's clock like its
 * pages and clicks. Otherwise the ticket's stored time, which the API
 * writes as a UTC wall time with no zone ("2026-10-22 06:35:08.215"), read
 * as UTC rather than as the browser's local time.
 */
function reportMoment(trail: Partial<SessionTrail>, stored: string | undefined): number | null {
  const local = trail.setup?.localTime;
  if (typeof local === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?[+-]\d{2}:\d{2}$/.test(local)) {
    const at = Date.parse(local);
    if (isEpochMs(at)) return at;
  }
  if (!stored) return null;
  const zoneless = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(stored);
  const at = Date.parse(zoneless ? `${stored.replace(' ', 'T')}Z` : stored);
  return isEpochMs(at) ? at : null;
}

/**
 * A ticket's trail in one line for Capture details: "2 pages and 14 clicks
 * before reporting, 1 rage click". Null when there is no trail.
 */
export function sessionSummary(session: unknown): string | null {
  if (!session || typeof session !== 'object') return null;
  const trail = session as Partial<SessionTrail>;
  const pages = listOf(trail.pages).length;
  const actions = listOf<SessionAction>(trail.actions);
  const clicks = actions.filter((action) => action.kind === 'click');
  if (pages === 0 && clicks.length === 0) return null;
  const rage = clicks.filter((click) => click.rage === true).length;
  const parts = [plural(pages, 'page'), plural(clicks.length, 'click')].filter(Boolean).join(' and ');
  return `${parts} before reporting${rage > 0 ? `, ${plural(rage, 'rage click')}` : ''}`;
}

function plural(count: number, noun: string): string {
  return count === 0 ? '' : `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * Where a link click went, when the trail kept no destination for it
 * (t-GvENEF): on a site the person has typed on, the trail reads none of the
 * page's text, so a click is only "a link in a sidebar". The page the app
 * moved to straight after says where it led, and is already in the trail, so
 * naming it reveals nothing new. Only a nameless link with no `goes` of its own, only
 * a move within the app no more than LINK_LED_MS after it, and only when no
 * other click came between them.
 */
const LINK_LED_MS = 1_500;

function whereLinksLed(actions: SessionAction[], pages: Array<{ at?: unknown; url?: unknown; via?: unknown }>): Map<number, string> {
  const moves = pages
    .filter((page): page is { at: number; url: string; via?: unknown } => isEpochMs(page.at) && typeof page.url === 'string' && pageVia(page.via) === 'in_app')
    .sort((a, b) => a.at - b.at);
  const clicks = actions
    .map((action, index) => ({ action, index }))
    .filter(({ action }) => action.kind === 'click' && isEpochMs(action.at))
    .sort((a, b) => a.action.at - b.action.at);
  const led = new Map<number, string>();
  clicks.forEach(({ action, index }, position) => {
    if (action.kind !== 'click' || (action.role !== 'a' && action.role !== 'link') || trailLinkShape(action.goes)) return;
    // A link that kept its name says what it was already; only a nameless one needs telling.
    if (visibleLength(action.name) > 0) return;
    const next = clicks[position + 1]?.action.at ?? Number.POSITIVE_INFINITY;
    const move = moves.find((page) => page.at >= action.at && page.at - action.at <= LINK_LED_MS && page.at <= next);
    if (!move) return;
    let path: string;
    try { path = new URL(move.url).pathname; } catch { return; }
    const shape = trailLinkShape(path);
    if (shape) led.set(index, shape);
  });
  return led;
}

function actionEntry(action: SessionAction, index: number, ledTo?: string): TimelineEntry | null {
  const base = { at: action.at, kind: 'action' as const, index, failed: false };
  switch (action.kind) {
    case 'click': {
      const flag = action.rage === true ? 'rage click' as const : action.possiblyDead === true ? 'possibly dead click' as const : undefined;
      // A test hook beside a kept name is the steadiest way back to the element (TOADO-927).
      const hook = visibleLength(action.name) > 0 && !isMasked(action.name) ? testHook(action.hook) : undefined;
      const locator = hook ? hookLocator(hook) : roleLocator(action.role, action.name);
      const goes = trailLinkShape(action.goes) ?? ledTo;
      const took = respondMs(action.respondMs);
      // A tab not already selected was switched to (Jason, 24 Oct); its state says no more then.
      const state = trailState(action.state);
      const switched = action.role === 'tab' && state?.selected === false;
      return {
        ...base,
        label: line(`${switched ? 'switched to' : 'clicked'} ${controlWords(action.name, action.role)}${goes ? ` (to ${goes})` : ''}${inArea(action.within)}${switched ? '' : stateWords(action.state)}${slowWords(took)}`),
        ...(took !== undefined ? { respondMs: took } : {}),
        ...(flag ? { flag } : {}),
        ...(locator ? { locator } : {}),
        ...(goes ? { goes } : {}),
      };
    }
    case 'change':
      return { ...base, label: line(`changed ${controlWords(action.name, action.role)}${inArea(action.within)}`) };
    case 'visibility':
      return { ...base, label: action.state === 'hidden' ? 'left the tab' : 'came back to the tab' };
    case 'connection':
      return { ...base, label: action.state === 'offline' ? 'went offline' : 'came back online' };
    case 'key': {
      const key = trailKey(action.key);
      const took = respondMs(action.respondMs);
      return key ? { ...base, label: line(`pressed ${key}${inArea(action.within)}${slowWords(took)}`), ...(took !== undefined ? { respondMs: took } : {}) } : null;
    }
    case 'submit':
      return { ...base, label: line(`sent a form${inArea(action.within)}`) };
    case 'resize': {
      const size = Number.isFinite(action.width) && Number.isFinite(action.height) ? `${Math.round(action.width)}×${Math.round(action.height)}` : '';
      return { ...base, label: line(`window became ${size}${action.orientation ? `, ${action.orientation}` : ''}`) };
    }
    default:
      return null;
  }
}

/**
 * The role a locator asks for, by the trail's role: a native tag whose role is certain, or an
 * interactive ARIA role the page gave it, which no native tag shares a name with. Any other role
 * (`element`, `div`, `menu`, whose tag and role differ) has no locator (Codex, review of TOADO-929).
 */
const LOCATOR_ROLES: Readonly<Record<string, string>> = {
  // An anchor is recorded as one only with an address. Fields are left out: their recorded name is
  // their label, placeholder or name attribute, which need not be their accessible name.
  a: 'link', button: 'button',
  link: 'link', tab: 'tab', menuitem: 'menuitem', menuitemcheckbox: 'menuitemcheckbox', menuitemradio: 'menuitemradio',
  // Not the roles a field can take (textbox, searchbox, combobox, slider, spinbutton): a field's
  // recorded name is its label, placeholder or name attribute, which need not be its accessible name.
  // Nor checkbox, radio or switch: an input taking one of those roles is named by its field label.
  option: 'option', treeitem: 'treeitem', gridcell: 'gridcell',
};

/** `getByRole('button', { name: "Pay now" })`, or none without a name, with a masked one, or without a role to name. */
function roleLocator(role: unknown, name: unknown): string | undefined {
  const text = typeof name === 'string' ? name.replace(/\s+/g, ' ').trim() : '';
  // A cut name ends in an ellipsis the element's own name does not have (Codex, review of TOADO-929).
  if (typeof role !== 'string' || visibleLength(text) === 0 || isMasked(text) || text.endsWith('…')) return undefined;
  const ariaRole = Object.prototype.hasOwnProperty.call(LOCATOR_ROLES, role) ? LOCATOR_ROLES[role]! : undefined;
  return ariaRole ? `getByRole('${ariaRole}', { name: ${JSON.stringify(text)}, exact: true })` : undefined;
}



/**
 * A locator by the developer's test hook (TOADO-927): Playwright's `getByTestId` reads
 * `data-testid` by default, so any other attribute is asked for by name. The value is held to
 * letters, digits and `- _ . :` (`testHook`), and both go in as string literals.
 */
function hookLocator(hook: TestHook): string {
  return hook.attr === 'data-testid'
    ? `getByTestId(${JSON.stringify(hook.value)})`
    : `locator(${JSON.stringify(`[${hook.attr}="${hook.value}"]`)})`;
}

/** A slow answer as words (TOADO-924): ", 1.8 s before the page answered", or nothing. */
function slowWords(value: unknown): string {
  const ms = respondMs(value);
  return ms === undefined ? '' : `, ${(ms / 1000).toFixed(1)} s before the page answered`;
}

/** How a page was reached, as the verb its line opens with (TOADO-921). */
const PAGE_VERBS = { load: 'opened', reload: 'reloaded', back_forward: 'went back or forward to', in_app: 'moved within the app to' } as const;

/**
 * A control's state when clicked (TOADO-920), as words: " (was disabled, collapsed)", or nothing.
 * "Was", since it is the state the click found, before the page answered it (Jason, 24 Oct).
 */
function stateWords(value: unknown): string {
  const state = trailState(value);
  if (!state) return '';
  const words = [
    state.disabled && 'disabled',
    state.readOnly && 'read only',
    state.expanded === true && 'expanded',
    state.expanded === false && 'collapsed',
    state.pressed === true && 'pressed',
    state.pressed === false && 'not pressed',
    state.pressed === 'mixed' && 'partly pressed',
    state.selected === true && 'selected',
  ].filter((word): word is string => typeof word === 'string');
  return words.length > 0 ? ` (was ${words.join(', ')})` : '';
}

/** Where on the page, as a person would say it: " in the navigation", or nothing. */
function inArea(within: unknown): string {
  const area = trailArea(within);
  return area ? ` ${AREA_WORDS[area]}` : '';
}

const AREA_WORDS: Readonly<Record<TrailArea, string>> = {
  navigation: 'in the navigation', header: 'in the header', footer: 'in the footer', sidebar: 'in a sidebar',
  dialog: 'in a dialog', form: 'in a form', search: 'in the search', menu: 'in a menu', toolbar: 'in a toolbar',
  table: 'in a table',
};

/**
 * The kind of control a named one is, said after its name (Jason, 24 Oct): the "Billing" tab,
 * the "Save" button. Only for roles a person would name that way; any other is its name alone.
 */
const CONTROL_NOUNS: Readonly<Record<string, string>> = {
  tab: 'tab', button: 'button', link: 'link', a: 'link', checkbox: 'checkbox', radio: 'radio button',
  switch: 'switch', menuitem: 'menu item', menuitemcheckbox: 'menu item', menuitemradio: 'menu item',
  option: 'option', treeitem: 'item', combobox: 'dropdown', select: 'dropdown', listbox: 'list',
  slider: 'slider', spinbutton: 'number field', textbox: 'field', searchbox: 'search box', textarea: 'text box',
  summary: 'section', row: 'row', gridcell: 'cell', cell: 'cell',
};

/** A control as a person would say it: the "Billing" tab, "Help" where its kind adds nothing, "a link" with no name. */
function controlWords(name: unknown, role: unknown): string {
  const named = quoted(name, role);
  if (!named.startsWith('"')) return named;
  const noun = typeof role === 'string' && Object.prototype.hasOwnProperty.call(CONTROL_NOUNS, role) ? CONTROL_NOUNS[role] : undefined;
  return noun ? `the ${named} ${noun}` : named;
}

/** An element as a person would name it: its name in quotes, or what it is where it has none ("a link"). */
function quoted(name: unknown, role: unknown): string {
  const text = typeof name === 'string' ? name.trim() : '';
  if (text) return `"${text}"`;
  return trailRoleWords(role);
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? value.filter((row): row is T => !!row && typeof row === 'object') : [];
}

/** One line of text, cut to fit a row. */
function line(value: unknown): string {
  const text = typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
  return text.length > MAX_LABEL ? `${text.slice(0, MAX_LABEL - 1)}…` : text;
}

/** An address as host, path and query, or as sent when it is not one. */
function requestPath(url: unknown): string {
  if (typeof url !== 'string') return '';
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}
